import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { formatDateTime } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, X } from "lucide-react";

export default function AuditTrail() {
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ entities: [], actions: [], users: [] });
  const [total, setTotal] = useState(0);
  const [f, setF] = useState({ entity: "all", action: "all", user_email: "all", date_from: "", date_to: "", q: "" });

  useEffect(() => { api.get("/audit-logs/meta").then(r => setMeta(r.data)).catch(() => {}); }, []);

  const load = useCallback(async () => {
    try {
      const p = new URLSearchParams();
      if (f.entity !== "all") p.set("entity", f.entity);
      if (f.action !== "all") p.set("action", f.action);
      if (f.user_email !== "all") p.set("user_email", f.user_email);
      if (f.date_from) p.set("date_from", f.date_from);
      if (f.date_to) p.set("date_to", f.date_to);
      if (f.q) p.set("q", f.q);
      const r = await api.get(`/audit-logs?${p.toString()}`);
      setRows(r.data);
      setTotal(r.data.length);
    } catch (e) {
      console.error("Gagal memuat audit trail:", e);
    }
  }, [f]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const reset = () => setF({ entity: "all", action: "all", user_email: "all", date_from: "", date_to: "", q: "" });
  const hasFilter = f.entity !== "all" || f.action !== "all" || f.user_email !== "all" || f.date_from || f.date_to || f.q;

  return (
    <div data-testid="page-audit" className="space-y-6">
      <div>
        <div className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-2">Jejak Audit</div>
        <h1 className="font-display text-3xl font-bold text-slate-900">Audit Trail</h1>
        <p className="text-slate-500 text-sm mt-1">Setiap perubahan data tercatat — siapa, kapan, apa yang berubah.</p>
      </div>

      <Card className="p-4 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2 items-end" data-testid="audit-filters">
        <div className="col-span-2"><Label className="text-xs">Cari detail (no. dokumen, nama, nominal)</Label>
          <div className="relative"><Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input className="pl-8" placeholder="cth: INV-2026, Budi, 50000" value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} data-testid="audit-search" />
          </div>
        </div>
        <div><Label className="text-xs">Entitas</Label>
          <Select value={f.entity} onValueChange={(v) => setF({ ...f, entity: v })}>
            <SelectTrigger data-testid="audit-filter-entity"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">Semua</SelectItem>{meta.entities.map(e => <SelectItem key={e} value={e}>{e}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><Label className="text-xs">Aksi</Label>
          <Select value={f.action} onValueChange={(v) => setF({ ...f, action: v })}>
            <SelectTrigger data-testid="audit-filter-action"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">Semua</SelectItem>{meta.actions.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><Label className="text-xs">User</Label>
          <Select value={f.user_email} onValueChange={(v) => setF({ ...f, user_email: v })}>
            <SelectTrigger data-testid="audit-filter-user"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">Semua</SelectItem>{meta.users.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="flex gap-2">
          <div className="flex-1"><Label className="text-xs">Dari</Label><Input type="date" value={f.date_from} onChange={(e) => setF({ ...f, date_from: e.target.value })} data-testid="audit-date-from" /></div>
          <div className="flex-1"><Label className="text-xs">Sampai</Label><Input type="date" value={f.date_to} onChange={(e) => setF({ ...f, date_to: e.target.value })} data-testid="audit-date-to" /></div>
        </div>
      </Card>

      <div className="flex items-center gap-3 text-xs text-slate-500">
        <span data-testid="audit-count"><span className="font-mono font-bold text-slate-800">{total}</span> log ditampilkan</span>
        {hasFilter && <Button variant="ghost" size="sm" className="h-6 text-xs" onClick={reset} data-testid="audit-reset"><X className="h-3 w-3 mr-1" />Reset Filter</Button>}
      </div>

      <Card>
        <table className="w-full text-sm" data-testid="table-audit">
          <thead className="bg-slate-50 text-xs font-mono uppercase tracking-wider text-slate-500 text-left"><tr><th className="py-2 px-4">Waktu</th><th>User</th><th>Entitas</th><th>Aksi</th><th>Detail</th></tr></thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.log_id} className="border-b">
                <td className="py-2 px-4 font-mono text-xs whitespace-nowrap">{formatDateTime(r.created_at)}</td>
                <td>{r.user_email}</td>
                <td><Badge variant="outline" className="font-mono text-xs">{r.entity}</Badge></td>
                <td>{r.action}</td>
                <td className="text-xs text-slate-500 font-mono">{JSON.stringify(r.after || r.before || {}).slice(0, 80)}</td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={5} className="py-8 text-center text-slate-400">Tidak ada log yang cocok dengan filter</td></tr>}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
